import { buildQuery } from '../../utils/buildQuery'
import { coldPalette } from '../../utils/colors'
import { localeISO } from '../../utils/helpers/date'
import {
  driverStatusTranslate,
  offerStatusTranslate
} from '../../utils/helpers/translate'
import { REQUEST, SUCCESS, ERROR, ROOT } from './types'

const labelAxes = {
  offersPerClient: { xAxis: 'status', stack: 'shipper' },
  offersPerRegional: { xAxis: 'status', stack: 'regional' },
  captation: { xAxis: 'offerStatus', stack: 'driverStatus' }
}

const parseData = (data = [], labels = {}) => {
  const labelsObject = data.reduce((lob, { _id, count }) => {
    // _id is string:
    if (typeof _id === 'string') {
      lob[_id] || (lob[_id] = 0)
      lob[_id] += count
      return lob
    }
    // _id is object:
    const xLabel = _id[labels.xAxis]
    if (labels.stack) {
      // group each corresponding data under xLabel
      const stackLabel = _id[labels.stack]
      lob[xLabel] || (lob[xLabel] = {})
      lob[xLabel][stackLabel] || (lob[xLabel][stackLabel] = {})
      lob[xLabel][stackLabel] = count
    } else {
      // sum everything from xLabel
      lob[xLabel] || (lob[xLabel] = 0)
      lob[xLabel] += count
    }
    return lob
  }, {})

  return labelsObject
}

const matchLabels = (data, labels) => {
  return labels.map((label) => data[label] || 0)
}

const getLabelsFromStack = (data) => {
  let labels = {}
  Object.values(data).forEach((set) => (labels = { ...labels, ...set }))
  return Object.keys(labels)
}

const convertToDataset = (
  data,
  _labels = {},
  translations = {},
  colors = coldPalette
) => {
  const parsedData = parseData(data, _labels)
  const chartData = Object.entries(parsedData).reduce(
    (obj, [key, value]) => ({ ...obj, [translations[key] || key]: value }),
    {}
  )

  if (_labels.stack) {
    const originalLables = getLabelsFromStack(chartData)
    const labels = originalLables.map((s) => translations[s] || s)
    return {
      labels,
      datasets: Object.entries(chartData).map(([label, data], i) => ({
        label,
        data: matchLabels(data, originalLables),
        backgroundColor: colors[i]
      })),
      total: Object.values(chartData)
        .map(Object.values)
        .flat()
        .reduce((total, num) => total + num, 0)
    }
  } else {
    const labels = Object.keys(chartData).map((s) => translations[s] || s)
    return {
      labels,
      datasets: [
        {
          data: Object.values(chartData),
          backgroundColor: colors
        }
      ],
      total: Object.values(chartData).reduce((total, num) => total + num, 0)
    }
  }
}

const generateChartMetaState = (def = () => {}) => ({
  totalOffers: def(),
  offerWeight: def(),
  offersPerClient: def(),
  offersPerRegional: def(),
  offersRegionalWeight: def(),
  newDrivers: def(),
  captation: def(),
  interestedDrivers: def(),
  countVehiclesByType: def(),
  countOffersByType: def(),
  vehicleAverageAge: def()
})

const state = () => ({
  data: generateChartMetaState(() => []),
  filters: {
    ...generateChartMetaState(() => ''),
    // initial_date: localeISO(false, new Date(Date.now() - 86400000)), // 86400000 = 1 dia
    initial_date: localeISO(false),
    final_date: localeISO(false)
  },
  loading: generateChartMetaState(() => false),
  error: generateChartMetaState(() => false)
})

const getters = {
  totalOffers: (state) =>
    convertToDataset(state.data.totalOffers, {
      ...offerStatusTranslate,
      ...driverStatusTranslate
    }),
  offersPerClient: (state) =>
    convertToDataset(state.data.offersPerClient, labelAxes.offersPerClient, {
      ...offerStatusTranslate,
      ...driverStatusTranslate
    }),
  offerWeight: (state) => convertToDataset(state.data.offerWeight),
  offersPerRegional: (state) =>
    convertToDataset(
      state.data.offersPerRegional,
      labelAxes.offersPerRegional,
      { ...offerStatusTranslate, ...driverStatusTranslate }
    ),
  offersRegionalWeight: (state) => {
    //convertToDataset(state.data.offersRegionalWeight, labelAxes.offersPerRegional)
    let labels = []
    let peso = []
    let i
    for (const dataDelected of state.data.offersRegionalWeight) {
      labels.push(dataDelected._id.regional)
      peso.push(dataDelected._id.count)
      i++
    }
    return {
      labels: labels,
      datasets: [
        {
          label: [],
          data: peso,
          backgroundColor: coldPalette
        }
      ],
      total: i
    }
  },
  newDrivers: (state) => convertToDataset(state.data.newDrivers),
  captation: (state) =>
    convertToDataset(state.data.captation, labelAxes.captation, {
      ...offerStatusTranslate,
      ...driverStatusTranslate
    }),
  interestedDrivers: (state) =>
    convertToDataset(
      state.data.interestedDrivers,
      {},
      { ...offerStatusTranslate, ...driverStatusTranslate }
    ),
  countVehiclesByType: (state) =>
    convertToDataset(state.data.countVehiclesByType),
  countOffersByType: (state) => convertToDataset(state.data.countOffersByType),
  vehicleAverageAge: (state) => convertToDataset(state.data.vehicleAverageAge),
  filters: (state) => state.filters,
  loading: (state) => state.loading,
  isLoading: (state) => Object.values(state.loading).some((loading) => loading),
  error: (state) => state.error
}

const actions = {
  async getDashboard({ commit, dispatch, state }, { chart }) {
    if (state.loading[chart]) return
    commit(REQUEST, chart)
    const res = await dispatch(
      `api`,
      {
        entity: `charts`,
        action: chart,
        query: buildQuery(state.filters)
      },
      ROOT
    )

    if (res?.status < 400) {
      commit(SUCCESS, { chart, data: res.data })
    } else {
      commit(ERROR, { chart, data: res })
    }

    return res
  },
  async init({ commit, dispatch, state }, filters = {}) {
    Object.entries(filters).forEach(([filter, value]) =>
      commit('QUERY', { filter, value })
    )

    Object.keys(state.data).forEach((chart) =>
      dispatch('getDashboard', { chart })
    )
  }
}

const mutations = {
  [REQUEST](state, chart) {
    state.loading[chart] = true
  },
  [SUCCESS](state, { chart, data }) {
    state.data[chart] = data
    state.loading[chart] = false
    state.error[chart] = false
  },
  [ERROR](state, { chart, error }) {
    state.error[chart] = error
    state.loading[chart] = false
  },
  QUERY(state, { filter, value }) {
    if (filter in state.filters) state.filters[filter] = value
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
